import { createModel } from '@rematch/core';

// Types
import type { RootModel } from './index';
import {
  IApplyControllerSetupPayload,
  IApplyControllerSetupResult,
  IControllerSetup,
  IControllerSetupConfig,
} from '../../types/controller-setup-types';

import http from '../http/http-common';
import { generatePath } from 'react-router-dom';
import URL from '../../constants/api-constants';
import { toast } from 'react-toastify';

const DEFAULT_STATE: {
  controllerSetups: IControllerSetup[] | null;
  controllerSetup: IControllerSetup | null;
} = {
  controllerSetups: null,
  controllerSetup: null,
};

export const controller_setups = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    setOne(state, payload) {
      return {
        ...state,
        controllerSetup: payload,
      };
    },
    setList(state, payload) {
      return {
        ...state,
        controllerSetups: payload,
      };
    },
    removeFromList(state, id: string) {
      return {
        ...state,
        controllerSetups: (state.controllerSetups ?? []).filter((item) => item.id !== id),
      };
    },
    resetOne(state) {
      return {
        ...state,
        controllerSetup: DEFAULT_STATE.controllerSetup,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getControllerSetups() {
        try {
          const response = await http.get(URL.CONTROLLER_SETUPS);
          if (response.status === 200) {
            dispatch.controller_setups.setList(response.data);
          }
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async getControllerSetup(id: string) {
        try {
          const response = await http.get(generatePath(URL.CONTROLLER_SETUP, { id }));
          if (response.status === 200) {
            dispatch.controller_setups.setOne(response.data);
          }
          return response.data as IControllerSetup;
        } catch (error: any) {
          toast.error(error.message);
          return undefined;
        }
      },

      async createControllerSetupFromController({ controllerId, name }: { controllerId: string; name: string }) {
        try {
          const response = await http.post(generatePath(URL.CONTROLLER_SETUP_FROM_CONTROLLER, { controllerId }), {
            name,
          });
          toast.success('Controller setup created');
          return response.data as IControllerSetup;
        } catch (error: any) {
          toast.error(error.message || 'Failed to create controller setup');
          return undefined;
        }
      },

      async updateControllerSetup({
        id,
        name,
        config,
      }: {
        id: string;
        name?: string;
        config?: IControllerSetupConfig;
      }) {
        try {
          const response = await http.put(generatePath(URL.CONTROLLER_SETUP, { id }), { name, config });
          toast.success('Controller setup updated');
          dispatch.controller_setups.setOne(response.data);
          return response.data as IControllerSetup;
        } catch (error: any) {
          toast.error(error.message || 'Failed to update controller setup');
          return undefined;
        }
      },

      async deleteControllerSetup({ id }: { id: string }) {
        try {
          await http.delete(generatePath(URL.CONTROLLER_SETUP, { id }));
          dispatch.controller_setups.removeFromList(id);
          toast.success('Controller setup deleted');
          return true;
        } catch (error: any) {
          toast.error(error.message || 'Failed to delete controller setup');
          return false;
        }
      },

      async applyControllerSetup({ id, payload }: { id: string; payload: IApplyControllerSetupPayload }) {
        try {
          const response = await http.post(generatePath(URL.CONTROLLER_SETUP_APPLY, { id }), payload);
          return response.data as IApplyControllerSetupResult[];
        } catch (error: any) {
          toast.error(error.message || 'Failed to apply controller setup');
          return undefined;
        }
      },
    };
  },
});
